import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box, Button, Card, CardContent, Checkbox, Chip, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, MenuItem,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import EmptyState from '../../../shared/components/EmptyState';
import AppDialog from '../../../shared/components/AppDialog';
import BulkActionStatusDialog from './BulkActionStatusDialog';
import { dataTableSx, HeadLabelCell, tableActionCellSx } from '../../../shared/components/TableHeader';
import { formatTonne } from '../../../shared/utils/number';
import { SCOPE_TOKENS } from '../../../theme/muiTheme';
import {
  ACTION_PLAN_SCOPE_VALUES,
  ACTION_PLAN_CATEGORY_LABELS,
  ACTION_PLAN_SCOPE_LABELS,
  ACTION_PLAN_STATUS_LABELS,
  ACTION_PLAN_STATUS_VALUES,
  commonPlanStatusOptions,
  estimateForFiscalYear,
  planAppliesToFiscalYear,
} from './actionPlanModel';
import { resultStatusLabelKey } from '../model/actionStatusModel';

const SCOPE_FILTERS = ['all', ...ACTION_PLAN_SCOPE_VALUES, 'NOT_APPLICABLE'];
const STATUS_FILTERS = ['all', ...ACTION_PLAN_STATUS_VALUES];

const SCOPE_TONE = {
  SCOPE_1: SCOPE_TOKENS.SCOPE_1,
  SCOPE_2: SCOPE_TOKENS.SCOPE_2,
  SCOPE_3: SCOPE_TOKENS.SCOPE_3,
};

function ScopeChip({ scope }) {
  const theme = useTheme();
  const tone = SCOPE_TONE[scope];
  if (!tone) {
    return <Chip label={ACTION_PLAN_SCOPE_LABELS[scope] || scope} size="small" />;
  }
  return (
    <Chip
      size="small"
      label={ACTION_PLAN_SCOPE_LABELS[scope] || scope}
      sx={{
        bgcolor: theme.palette.mode === 'dark' ? alpha(tone.color, 0.18) : tone.bg,
        color: theme.palette.mode === 'dark' ? tone.color : tone.dark,
      }}
    />
  );
}

const statusChipColor = status => {
  if (status === 'COMPLETED') return 'success';
  if (status === 'IN_PROGRESS') return 'warning';
  if (status === 'CANCELLED') return 'error';
  return 'default';
};

const boundaryLabel = plan => plan.boundary?.name || '—';

const estimatedActivityLabel = estimate => {
  if (!estimate?.estimatedActivity) return '—';
  return `${estimate.estimatedActivity} ${estimate.activityUnit || ''}`.trim();
};

const annualResultForYear = (plan, fiscalYear) => (
  plan.annualResults?.find(result => result.fiscalYear === fiscalYear) || null
);

const resultStatusColor = status => {
  if (status === 'VERIFIED') return 'success';
  if (status === 'REJECTED') return 'error';
  return 'warning';
};

/** Manage multi-year action plans in the selected FY context with backend filters. */
export default function ActionPlanList({
  actionPlans, canMutate, error, initialFilter = 'all', loading,
  onAdd, onBulkTransition, onDelete, onEdit, onOpenResult, onSearch, onView, selectedYear, t,
  yearSelector = null,
}) {
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [planPendingRemoval, setPlanPendingRemoval] = useState(null);
  const [removalDialogOpen, setRemovalDialogOpen] = useState(false);
  const searchTimerRef = useRef(null);

  const fiscalYear = Number(selectedYear);

  const emitSearch = useCallback((nextFilters) => {
    onSearch?.({
      fiscal_year: fiscalYear,
      scope: nextFilters.scope === 'all' ? null : nextFilters.scope,
      status: nextFilters.status === 'all' ? null : nextFilters.status,
      search: nextFilters.search || null,
      page: 1,
      page_size: 50,
    });
  }, [fiscalYear, onSearch]);

  useEffect(() => {
    setSearch('');
    setScopeFilter('all');
    setStatusFilter(initialFilter === 'active' ? 'IN_PROGRESS' : 'all');
    setSelectedPlanIds([]);
    setPage(0);
    setStatusDialogOpen(false);
    setRemovalDialogOpen(false);
  }, [fiscalYear, initialFilter]);

  useEffect(() => {
    window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => {
      emitSearch({ scope: scopeFilter, status: statusFilter, search });
    }, 400);
    return () => window.clearTimeout(searchTimerRef.current);
  }, [search, scopeFilter, statusFilter, fiscalYear, emitSearch]);

  useEffect(() => {
    setPage(0);
  }, [search, scopeFilter, statusFilter]);

  const filteredPlans = useMemo(() => actionPlans
    .filter(plan => planAppliesToFiscalYear(plan, fiscalYear)), [actionPlans, fiscalYear]);

  const selectedPlans = useMemo(() => {
    const selectedIds = new Set(selectedPlanIds);
    return filteredPlans.filter(plan => selectedIds.has(plan.id));
  }, [filteredPlans, selectedPlanIds]);

  const statusOptions = useMemo(() => commonPlanStatusOptions(selectedPlans), [selectedPlans]);

  const applyBulkStatus = (nextStatus) => {
    const planIds = selectedPlans.map(plan => plan.id);
    if (!planIds.length || !statusOptions.includes(nextStatus)) return;
    onBulkTransition?.(planIds, nextStatus);
    setSelectedPlanIds([]);
    setStatusDialogOpen(false);
  };

  const confirmRemove = () => {
    if (!planPendingRemoval) return;
    onDelete?.(planPendingRemoval.id);
    setRemovalDialogOpen(false);
  };

  const toggleSelect = (planId, checked) => {
    setSelectedPlanIds(current => (
      checked ? [...current, planId] : current.filter(id => id !== planId)
    ));
  };

  const allVisibleSelected = filteredPlans.length > 0
    && filteredPlans.every(plan => selectedPlanIds.includes(plan.id));
  const pagePlans = filteredPlans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Card component={'section'} sx={{
      bgcolor: 'background.paper',
      borderColor: 'divider',
      boxShadow: theme => (theme.palette.mode === 'dark'
        ? 'none'
        : '0 10px 30px rgba(24, 39, 51, 0.045)'),
      borderTop: 4,
      borderTopColor: 'success.main',
      overflow: 'hidden',
    }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent={'space-between'} spacing={2} sx={{ p: { xs: 2, md: 2.5 }, pb: 1.75 }}>
          <Stack direction={'row'} alignItems={'center'} spacing={1.25} sx={{ minWidth: 0 }}>
            <ChecklistOutlinedIcon color={'primary'} />
            <Stack spacing={0.15}>
              <Typography component={'h2'} sx={{ fontSize: 18, fontWeight: 700, overflowWrap: 'anywhere' }}>
                {t('climateAction.actions.planTitle')} · FY{fiscalYear}
              </Typography>
            </Stack>
          </Stack>
          {canMutate && (
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              {yearSelector}
              <Button startIcon={<AddRoundedIcon />} variant={'contained'} onClick={onAdd} sx={{ whiteSpace: 'nowrap' }}>
                {t('climateAction.actions.add')}
              </Button>
            </Stack>
          )}
        </Stack>

        <Box sx={{
          display: 'grid', gap: 1,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(300px, 1fr) repeat(2, minmax(170px, 210px))' },
          px: 2.5, pb: 1.5,
        }}>
          <TextField
            inputProps={{ 'aria-label': t('climateAction.filter.search') }}
            onChange={event => setSearch(event.target.value)}
            placeholder={t('climateAction.plan.searchPlaceholder')}
            size={'small'}
            value={search}
            InputProps={{
              startAdornment: <InputAdornment position={'start'}><SearchRoundedIcon color={'action'} fontSize={'small'} /></InputAdornment>,
            }}
          />
          <TextField
            inputProps={{ 'aria-label': t('climateAction.filter.scope') }}
            onChange={event => setScopeFilter(event.target.value)}
            select size={'small'} value={scopeFilter}
          >
            {SCOPE_FILTERS.map(value => (
              <MenuItem key={value} value={value}>
                {value === 'all' ? t('climateAction.filter.allScopes') : ACTION_PLAN_SCOPE_LABELS[value] || value}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            inputProps={{ 'aria-label': t('climateAction.filter.actionStatus') }}
            onChange={event => setStatusFilter(event.target.value)}
            select size={'small'} value={statusFilter}
          >
            {STATUS_FILTERS.map(value => (
              <MenuItem key={value} value={value}>
                {value === 'all'
                  ? t('climateAction.filter.allActionStatuses')
                  : ACTION_PLAN_STATUS_LABELS[value] || value}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {canMutate && (
          <Box
            aria-label={t('climateAction.bulk.deliveryToolbar')}
            role={'region'}
            sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider', px: 2.5, py: 1.25 }}
          >
            <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={1.25}>
              {selectedPlans.length > 0 && (
                <Typography color={'text.secondary'} sx={{ fontSize: 13 }}>
                  {t('climateAction.bulk.selectedCount', { count: selectedPlans.length })}
                </Typography>
              )}
              <Button
                disabled={selectedPlans.length === 0 || statusOptions.length === 0}
                onClick={() => setStatusDialogOpen(true)}
                startIcon={<PublishedWithChangesOutlinedIcon />}
                variant={'outlined'}
              >
                {t('climateAction.bulk.updateStatus')}
              </Button>
            </Stack>
          </Box>
        )}

        <TableContainer sx={{ overflowX: 'auto', scrollbarWidth: 'thin' }}>
          <Table stickyHeader size={'small'} sx={theme => ({ ...dataTableSx(theme), minWidth: 950 })}>
            <TableHead>
              <TableRow>
                <HeadLabelCell width={54}>
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={selectedPlanIds.length > 0 && !allVisibleSelected}
                    onChange={event => setSelectedPlanIds(
                      event.target.checked ? filteredPlans.map(plan => plan.id) : [],
                    )}
                    size="small"
                  />
                </HeadLabelCell>
                <HeadLabelCell width={116}>{t('climateAction.plan.actionColumn')}</HeadLabelCell>
                <HeadLabelCell>{t('climateAction.plan.title')}</HeadLabelCell>
                <HeadLabelCell width={150}>{t('climateAction.filter.scope')}</HeadLabelCell>
                <HeadLabelCell width={160}>{t('climateAction.plan.category')}</HeadLabelCell>
                <HeadLabelCell width={150}>{t('climateAction.plan.period')}</HeadLabelCell>
                <HeadLabelCell width={130}>{t('climateAction.plan.statusLabel')}</HeadLabelCell>
                <HeadLabelCell align={'right'} width={160}>{t('climateAction.plan.expectedReduction')}</HeadLabelCell>
                <HeadLabelCell align={'right'} width={150}>{t('climateAction.plan.estimatedActivity')}</HeadLabelCell>
                <HeadLabelCell width={130}>{t('climateAction.plan.resultStatus', { defaultValue: 'Result status' })}</HeadLabelCell>
                <HeadLabelCell align={'right'} width={130}>{t('climateAction.plan.result')}</HeadLabelCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {pagePlans.map((plan) => {
                  const annualResult = annualResultForYear(plan, fiscalYear);
                  return (
                <TableRow hover key={plan.id}>
                  <TableCell sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                    <Checkbox
                      checked={selectedPlanIds.includes(plan.id)}
                      onChange={event => toggleSelect(plan.id, event.target.checked)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={tableActionCellSx}>
                    <Stack direction={'row'} spacing={0.25}>
                      <Tooltip title={t('climateAction.table.viewDetails')}>
                           <IconButton aria-label={t('climateAction.table.viewDetails')} onClick={() => onView(plan)} size={'small'}>
                           <VisibilityOutlinedIcon fontSize={'small'} />
                         </IconButton>
                      </Tooltip>
                      {canMutate && plan.status !== 'COMPLETED' && (
                        <Tooltip title={t('climateAction.table.editAction')}>
                          <IconButton aria-label={t('climateAction.table.editAction')} onClick={() => onEdit(plan)} size={'small'}>
                            <EditOutlinedIcon fontSize={'small'} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canMutate && (
                        <Tooltip title={t('climateAction.table.recordResult')}>
                          <IconButton
                            aria-label={t('climateAction.table.recordResult')}
                            disabled={plan.status === 'DRAFT'}
                            onClick={() => onOpenResult(plan)}
                            size={'small'}
                          >
                            <AssessmentOutlinedIcon fontSize={'small'} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canMutate && plan.status === 'DRAFT' && (
                        <Tooltip title={t('climateAction.plan.remove')}>
                          <IconButton
                            aria-label={t('climateAction.plan.remove')}
                            onClick={() => {
                              setPlanPendingRemoval(plan);
                              setRemovalDialogOpen(true);
                            }}
                            size={'small'}
                          >
                            <DeleteOutlineRoundedIcon fontSize={'small'} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{plan.title}</Typography>
                    <Typography color={'text.secondary'} sx={{ fontSize: 12 }}>{boundaryLabel(plan)}</Typography>
                  </TableCell>
                  <TableCell>
                    <ScopeChip scope={plan.scope} />
                  </TableCell>
                  <TableCell>
                    <Typography color={'text.secondary'} sx={{ fontSize: 12.5 }}>
                      {plan.actionType === 'CARBON_CREDIT_RETIREMENT'
                        ? t('climateAction.plan.notApplicable')
                        : plan.subCategory || ACTION_PLAN_CATEGORY_LABELS[plan.category] || plan.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                       {plan.startFiscalYear} - {plan.endFiscalYear}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={statusChipColor(plan.status)}
                       label={ACTION_PLAN_STATUS_LABELS[plan.status] || plan.status}
                      size={'small'}
                      variant={'outlined'}
                    />
                  </TableCell>
                  <TableCell align={'right'}>
                    <Typography sx={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums', fontWeight: 650, whiteSpace: 'nowrap' }}>
                      {formatTonne(estimateForFiscalYear(plan, fiscalYear)?.reductionTco2ePerYear, { withUnit: true })}
                    </Typography>
                  </TableCell>
                  <TableCell align={'right'}>
                    <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {estimatedActivityLabel(estimateForFiscalYear(plan, fiscalYear))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {annualResult?.status ? (
                      <Chip
                        color={resultStatusColor(annualResult.status)}
                        label={t(resultStatusLabelKey(annualResult.status), { defaultValue: annualResult.status })}
                        size="small"
                        variant="outlined"
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell align={'right'}>
                    <Typography sx={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums', fontWeight: 650, whiteSpace: 'nowrap' }}>
                      {formatTonne(annualResult?.reportedTco2e, { withUnit: true })}
                    </Typography>
                  </TableCell>
                </TableRow>
                  );
                })}
              {filteredPlans.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={11} sx={{ border: 0 }}>
                    <EmptyState description={t('climateAction.actions.empty')} title={t('common.noResults')} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && <Box sx={{ height: 3, bgcolor: 'action.hover' }} aria-label={t('common.loading')} />}
        <TablePagination
          component="div"
          count={filteredPlans.length}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
        />

         <BulkActionStatusDialog
           actions={selectedPlans}
           onClose={() => setStatusDialogOpen(false)}
           onConfirm={applyBulkStatus}
           open={statusDialogOpen}
           statusOptions={statusOptions}
           t={t}
         />

        <AppDialog fullWidth maxWidth="xs" onClose={() => setRemovalDialogOpen(false)} open={removalDialogOpen}>
          <DialogTitle sx={{ fontSize: 20, fontWeight: 850, pr: 7 }}>{t('climateAction.plan.deleteTitle')}</DialogTitle>
          <DialogContent dividers>
            <Typography>{t('climateAction.plan.deleteMessage', { title: planPendingRemoval?.title })}</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button color="inherit" onClick={() => setRemovalDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button color="error" onClick={confirmRemove} variant="contained">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </AppDialog>
      </CardContent>
    </Card>
  );
}
